import { X } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";

/**
 * Information_LN Component
 * Displays a popup with calculation details and advisory information for Lightning Forecast.
 */
const Information_LN = () => {
    const [open, setOpen] = useState(false);
    const infoRef = useRef(null);
    const infoPanelRef = useCallback((node) => {
        infoRef.current = node;
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    // Leaflet's own drag/click handling swallows clicks on overlay controls
    // unless propagation to the map is explicitly disabled on the DOM node.
    const btnRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (infoRef.current && !infoRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const infoContent = (
        <div style={{ lineHeight: '1.4' }}>
            <h3 style={{ fontSize: '1.1em', marginTop: '10px', marginBottom: '8px', fontWeight: 'bold' }}>Advisory</h3>
            <ul style={{ listStyle: 'none', paddingLeft: '0', marginBottom: '15px' }}>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: 'rgb(148, 203, 85)' }}></span>
                    <strong>0:</strong> No Risk
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: 'rgb(243, 235, 18)' }}></span>
                    <strong>1 - 3:</strong> Low Risk
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: 'rgb(243, 155, 58)' }}></span>
                    <strong>3 - 10:</strong> Medium Risk
                </li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: 'rgb(230, 45, 58)' }}></span>
                    <strong>10 - 20+:</strong> High Risk
                </li>
            </ul>

            <hr style={{ margin: '15px 0', borderColor: '#e5e7eb' }} />

            <h3 style={{ fontSize: '1.1em', marginTop: '0', marginBottom: '8px', fontWeight: 'bold' }}>Lightning Flash Density (LFD)</h3>
            <p style={{ marginTop: '0', marginBottom: '10px', fontSize: '12px', color: '#4b5563' }}>
                The probability and intensity of lightning events based on atmospheric conditions.
            </p>

            <p style={{ fontWeight: 'bold', fontSize: '0.95em', marginBottom: '8px' }}>
                Formula:
            </p>
            <div style={{ background: '#f0f0f0', padding: '10px', borderRadius: '5px' }}>
                <code style={{ fontSize: '0.9em' }}>
                    LFD = (CAPE / 1000) × (showers × rain) × (cloud_cover / 100)
                </code>
            </div>

            <hr style={{ margin: '15px 0', borderColor: '#e5e7eb' }} />

            <h3 style={{ fontSize: '1.1em', marginTop: '0', marginBottom: '8px', fontWeight: 'bold' }}>Units of Measurement</h3>
            <ul style={{ listStyle: 'disc', marginLeft: '20px', paddingLeft: '0', fontSize: '12px', color: '#374151' }}>
                <li><strong>CAPE:</strong> Joules per kilogram (J/kg)</li>
                <li><strong>Cloud Cover:</strong> Percentage (%)</li>
                <li><strong>Rain:</strong> Millimeters (mm)</li>
                <li><strong>Showers:</strong> Millimeters (mm)</li>
                <li><strong>Time:</strong> ISO 8601 format</li>
            </ul>
        </div>
    );

    return (
        <div style={{ position: "absolute", bottom: "20px", left: "12px", zIndex: 1000 }}>
            {/* Info Button */}
            <button
                ref={btnRef}
                onClick={() => setOpen(!open)}
                title="More Info"
                style={{
                    background: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px) brightness(1.1)",
          WebkitBackdropFilter: "blur(10px) brightness(1.1)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
          borderRadius: "8px",
          padding: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: 16,
                    color: "white",
                    transition: "transform 0.2s",
                    paddingBottom: "7px",
                }}
                aria-label="Show info"
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
            >
                <FaCircleInfo />
            </button>

            {/* Info Card */}
            {open && (
                <div
                    ref={infoPanelRef}
                    style={{
                        position: "absolute",
                        bottom: 30,
                        left: 0,
                        minWidth: 300,
                        maxWidth: 450,
                        maxHeight: 350,
                        overflowY: 'auto',
                        background: "#fff",
                        borderRadius: 12,
                        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                        padding: "16px 18px",
                        fontSize: 13,
                        color: "#333",
                        zIndex: 1000,
                        animation: "fadeIn 0.3s ease",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <h5 style={{ fontWeight: 600, color: "#000", margin: 0 }}>Information</h5>
                        <button
                            onClick={() => setOpen(false)}
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                color: "#888",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                            aria-label="Close info"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div>{infoContent}</div>
                </div>
            )}

            {/* Fade-in Animation */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default Information_LN;
